import React, { useEffect } from 'react';
import { AlertTriangle, Check, Clock, X } from 'lucide-react';
import { formatTimeHHmm, type HourCellActivity, type HourLoadState } from '../services/lstMatrixHourly';
import { LST_STATUS_LABELS, type LstRotinaDoDia } from '../types';
import { SwiStandardView } from './SwiStandardView';

interface HourBlockDrawerProps {
  open: boolean;
  onClose: () => void;
  date: string | null;
  hourLabel: string | null;
  loadMin: number;
  loadState: HourLoadState | null;
  activities: HourCellActivity[];
  resolveUserName: (userId: string) => string;
}

const LOAD_LABELS: Record<HourLoadState, string> = {
  idle: 'Livre',
  partial: 'Ocupação parcial',
  saturated: 'Saturação ideal',
  overload: 'Sobrecarga',
};

const LOAD_CLS: Record<HourLoadState, string> = {
  idle: 'text-slate-400',
  partial: 'text-blue-600 dark:text-blue-400',
  saturated: 'text-emerald-600 dark:text-emerald-400',
  overload: 'text-red-600 dark:text-red-400',
};

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
  });
}

/** Drawer — atividades alocadas num bloco hora × dia (SOP + memória de campo). */
export const HourBlockDrawer: React.FC<HourBlockDrawerProps> = ({
  open,
  onClose,
  date,
  hourLabel,
  loadMin,
  loadState,
  activities,
  resolveUserName,
}) => {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open || !date || !hourLabel) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end">
      <button type="button" aria-label="Fechar" onClick={onClose} className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" />

      <aside
        role="dialog"
        aria-modal="true"
        className="relative flex h-full w-full max-w-[480px] flex-col border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-950"
        style={{ animation: 'slideInRight .18s ease-out' }}
      >
        <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <div>
            <h3 className="flex items-center gap-2 text-[14px] font-bold text-slate-800 dark:text-slate-100">
              <Clock size={16} className="text-blue-500" />
              Bloco {hourLabel}
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">{formatDate(date)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X size={18} />
          </button>
        </div>

        <div className="shrink-0 border-b border-slate-100 px-4 py-2 dark:border-slate-800">
          <div className="flex items-center gap-3 text-[11px]">
            <span className={`font-bold ${loadState ? LOAD_CLS[loadState] : ''}`}>
              {loadState ? LOAD_LABELS[loadState] : '—'}
            </span>
            <span className="text-slate-400">·</span>
            <span className="tabular-nums text-slate-600 dark:text-slate-300">{loadMin} min alocados</span>
            <span className="text-slate-400">·</span>
            <span className="text-slate-500">{activities.length} atividade(s)</span>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {activities.length === 0 ? (
            <p className="py-8 text-center text-[12px] italic text-slate-400">Hora livre — sem atividades agendadas.</p>
          ) : (
            <div className="space-y-3">
              {activities.map((a) => (
                <ActivityCard key={a.template.id} activity={a} resolveUserName={resolveUserName} />
              ))}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

const ActivityCard: React.FC<{
  activity: HourCellActivity;
  resolveUserName: (userId: string) => string;
}> = ({ activity, resolveUserName }) => {
  const { template, overlapMin, execucao } = activity;
  const rotina: LstRotinaDoDia = {
    template,
    execucao,
    status: execucao?.status ?? 'nao_realizado',
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <h4 className="text-[12px] font-bold text-slate-800 dark:text-slate-100">{template.title}</h4>
          <p className="text-[10px] text-slate-400">
            {formatTimeHHmm(template.scheduled_time) ?? '—'} · {overlapMin} min neste bloco · {template.duration_minutes ?? 15} min total
          </p>
        </div>
        {execucao && (
          <span className={`shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${
            execucao.status === 'realizado'
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
              : execucao.status === 'realizado_com_desvio'
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          }`}>
            {LST_STATUS_LABELS[execucao.status]}
          </span>
        )}
      </div>

      <div className="mb-2 rounded border border-slate-100 bg-white px-2 py-1.5 dark:border-slate-800 dark:bg-slate-950">
        <p className="mb-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">Padrão (SWI)</p>
        <SwiStandardView template={template} />
      </div>

      {execucao?.observacao && (
        <p className="mb-1 text-[11px] text-slate-600 dark:text-slate-300">
          <span className="font-semibold text-slate-400">Nota de campo: </span>{execucao.observacao}
        </p>
      )}
      {execucao?.justificativa_desvio && (
        <p className="flex items-start gap-1 text-[11px] italic text-amber-600 dark:text-amber-400">
          <AlertTriangle size={11} className="mt-[2px] shrink-0" /> {execucao.justificativa_desvio}
        </p>
      )}
      {execucao && (
        <p className="mt-1 text-[9px] text-slate-400">
          Executor: {resolveUserName(execucao.user_id)}
        </p>
      )}
      {!execucao && rotina.status === 'nao_realizado' && (
        <p className="mt-1 flex items-center gap-1 text-[10px] text-slate-400">
          <Check size={10} className="opacity-40" /> Sem registro de execução neste dia
        </p>
      )}
    </div>
  );
};

export default HourBlockDrawer;
